import { proposalSchema, type RevisionProposal } from "@termclear/contracts";

export type PendingTurnOperation = {
  content: string;
  key: string;
};

export type PendingApplyOperation = {
  proposalId: string;
  key: string;
};

export type StoredChatDraft = {
  schemaVersion: 2;
  conversationId: string | null;
  input: string;
  proposal: RevisionProposal | null;
  conversationOperationKey: string;
  autoTurnOperationKey: string;
  autoTurnPending: boolean;
  sendOperation: PendingTurnOperation | null;
  proposalOperationKey: string | null;
  applyOperation: PendingApplyOperation | null;
};

export type DraftOperationIdentity = {
  field: "conversationOperationKey" | "autoTurnOperationKey" | "sendOperation" | "proposalOperationKey" | "applyOperation";
  key: string;
};

export type DraftMutationResult = {
  kind: "written" | "stale" | "failed";
  draft: StoredChatDraft;
};

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const MAX_OPERATION_KEY_LENGTH = 128;

export function readChatDraft(
  storage: DraftStorage,
  draftId: string,
  createOperationKey: () => string,
): StoredChatDraft {
  const empty = newDraft(createOperationKey);
  try {
    const current = parseRecord(storage.getItem(currentDraftKey(draftId)));
    if (current?.schemaVersion === 2) return parseCurrentDraft(current, empty);

    const legacy = parseRecord(storage.getItem(legacyDraftKey(draftId)));
    if (!legacy) return empty;
    const conversationId = optionalString(legacy.conversationId);
    return {
      ...empty,
      conversationId,
      input: typeof legacy.input === "string" ? legacy.input : "",
      proposal: parseProposal(legacy.proposal),
      // A v1 draft did not retain the original auto-turn key. Treat it as settled
      // instead of manufacturing a new provider operation during migration.
      autoTurnPending: false,
    };
  } catch {
    return empty;
  }
}

export function writeChatDraft(storage: DraftStorage, draftId: string, draft: StoredChatDraft): boolean {
  try {
    storage.setItem(currentDraftKey(draftId), JSON.stringify(draft));
    storage.removeItem(legacyDraftKey(draftId));
    return true;
  } catch {
    return false;
  }
}

export function patchChatDraft(
  storage: DraftStorage,
  draftId: string,
  fallback: StoredChatDraft,
  patch: Partial<StoredChatDraft>,
  expected?: DraftOperationIdentity,
): DraftMutationResult {
  try {
    const stored = parseRecord(storage.getItem(currentDraftKey(draftId)));
    const current = stored?.schemaVersion === 2 ? parseCurrentDraft(stored, fallback) : fallback;
    if (expected && operationIdentity(current, expected.field) !== expected.key) {
      return { kind: "stale", draft: current };
    }
    const next = { ...current, ...patch };
    storage.setItem(currentDraftKey(draftId), JSON.stringify(next));
    storage.removeItem(legacyDraftKey(draftId));
    return { kind: "written", draft: next };
  } catch {
    return { kind: "failed", draft: fallback };
  }
}

export function removeChatDraft(
  storage: DraftStorage,
  draftId: string,
  fallback: StoredChatDraft,
  expected: DraftOperationIdentity,
): DraftMutationResult {
  try {
    const stored = parseRecord(storage.getItem(currentDraftKey(draftId)));
    const current = stored?.schemaVersion === 2 ? parseCurrentDraft(stored, fallback) : fallback;
    if (operationIdentity(current, expected.field) !== expected.key) {
      return { kind: "stale", draft: current };
    }
    storage.removeItem(currentDraftKey(draftId));
    storage.removeItem(legacyDraftKey(draftId));
    return { kind: "written", draft: current };
  } catch {
    return { kind: "failed", draft: fallback };
  }
}

function newDraft(createOperationKey: () => string): StoredChatDraft {
  return {
    schemaVersion: 2,
    conversationId: null,
    input: "",
    proposal: null,
    conversationOperationKey: createOperationKey(),
    autoTurnOperationKey: createOperationKey(),
    autoTurnPending: true,
    sendOperation: null,
    proposalOperationKey: null,
    applyOperation: null,
  };
}

function parseCurrentDraft(record: Record<string, unknown>, fallback: StoredChatDraft): StoredChatDraft {
  return {
    schemaVersion: 2,
    conversationId: optionalString(record.conversationId),
    input: typeof record.input === "string" ? record.input : "",
    proposal: parseProposal(record.proposal),
    conversationOperationKey: operationKey(record.conversationOperationKey) ?? fallback.conversationOperationKey,
    autoTurnOperationKey: operationKey(record.autoTurnOperationKey) ?? fallback.autoTurnOperationKey,
    autoTurnPending: record.autoTurnPending === true,
    sendOperation: pendingTurn(record.sendOperation),
    proposalOperationKey: operationKey(record.proposalOperationKey),
    applyOperation: pendingApply(record.applyOperation),
  };
}

function parseRecord(value: string | null): Record<string, unknown> | null {
  if (!value) return null;
  const parsed: unknown = JSON.parse(value);
  return isRecord(parsed) ? parsed : null;
}

function parseProposal(value: unknown): RevisionProposal | null {
  const checked = proposalSchema.safeParse(value);
  return checked.success ? checked.data : null;
}

function pendingTurn(value: unknown): PendingTurnOperation | null {
  if (!isRecord(value) || typeof value.content !== "string") return null;
  const key = operationKey(value.key);
  return key ? { content: value.content, key } : null;
}

function pendingApply(value: unknown): PendingApplyOperation | null {
  if (!isRecord(value) || typeof value.proposalId !== "string" || !value.proposalId) return null;
  const key = operationKey(value.key);
  return key ? { proposalId: value.proposalId, key } : null;
}

function operationKey(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 && value.length <= MAX_OPERATION_KEY_LENGTH
    ? value
    : null;
}

function operationIdentity(draft: StoredChatDraft, field: DraftOperationIdentity["field"]): string | null {
  const value = draft[field];
  if (typeof value === "string") return value;
  return value?.key ?? null;
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function currentDraftKey(draftId: string): string {
  return `termclear:chat-draft:v2:${draftId}`;
}

function legacyDraftKey(draftId: string): string {
  return `termclear:chat-draft:${draftId}`;
}
